// Version history: the page's versions kept by the server (the newest 30),
// newest first, as plain rows split by hairlines. "Version N" counts up from
// the oldest. A row is marked Current only when it holds exactly what the page
// holds now (the newest version can be a "Before AI edit" copy of older words);
// every other row can be restored (people who can edit only). Times read
// relative, with the full US Eastern time on hover.
import type { PageVersion } from '../api';
import { RotateCcwIcon } from '../icons';
import { EditorDrawer } from './EditorDrawer';

/** Every time in the editor reads in US Eastern. */
const ET = 'America/New_York';
const fullFmt = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: ET });
const shortFmt = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: ET });
const dateFmt = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: ET });
const yearFmt = new Intl.DateTimeFormat('en-US', { year: 'numeric', timeZone: ET });

/** "Oct 11, 2026, 10:43 AM ET". */
export function formatEasternDateTime(ts: number): string {
  return `${fullFmt.format(new Date(ts))} ET`;
}

/** "Just now", "5 min ago", "3 hr ago", "Yesterday", "2 days ago", then a short date. */
export function formatRelative(ts: number, now: number = Date.now()): string {
  const seconds = Math.round((now - ts) / 1000);
  if (seconds < 45) return 'Just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.round(hours / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  const d = new Date(ts);
  return yearFmt.format(d) === yearFmt.format(new Date(now)) ? shortFmt.format(d) : dateFmt.format(d);
}

const SOURCE: Record<string, string> = {
  Saved: 'Saved in the editor',
  Published: 'Published',
  Unpublished: 'Unpublished',
  'Before AI edit': 'Before an AI edit',
  'Before restore': 'Before a restore',
};

/** What made a version, from its stored label. */
export function versionSource(label: string): string {
  return SOURCE[label] ?? label;
}

/** A version's time as a timestamp (0 when unreadable). */
export function versionTime(v: Pick<PageVersion, 'created_at'>): number {
  const t = Date.parse(v.created_at);
  return Number.isFinite(t) ? t : 0;
}

export function HistoryDrawer({ open, versions, loading, error, onRestore, onClose, canRestore = true, isCurrent, now }: {
  open: boolean;
  /** Newest first, as the server sends them. Null while loading the first time. */
  versions: PageVersion[] | null;
  loading?: boolean;
  error?: string | null;
  /** Restore this version; `n` is its number in the list. */
  onRestore: (v: PageVersion, n: number) => void;
  onClose: () => void;
  /** False for people who can only view: no Restore buttons. */
  canRestore?: boolean;
  /** True when a version holds exactly what the page holds now (marked
   *  Current, no Restore). Without it no row is marked. */
  isCurrent?: (v: PageVersion) => boolean;
  /** Clock for the relative times (tests). */
  now?: number;
}) {
  const at = now ?? Date.now();
  const list = versions ?? [];
  return (
    <EditorDrawer open={open} onClose={onClose} title="Version history" width={400}>
      <p className="mb-3 text-xs text-textDim">A version is kept each time the page is published, and every 10 minutes while a draft saves.</p>
      {error && <p role="alert" className="mb-3 text-xs text-danger">{error}</p>}
      {list.length === 0 ? (
        <p className="text-xs text-textDim">{loading || versions === null ? 'Loading versions…' : 'No versions yet.'}</p>
      ) : (
        <ol className="m-0 list-none p-0" data-history-list>
          {list.map((v, i) => {
            const n = list.length - i;
            const ts = versionTime(v);
            const current = isCurrent?.(v) ?? false;
            return (
              <li key={v.id} className="flex items-center gap-3 border-b border-border py-3" data-version={n}>
                <div className="grid min-w-0 flex-1 gap-0.5">
                  <strong className="flex items-center text-sm font-medium text-text">
                    Version {n}
                    {current && <span className="pe-badge chip-gray ml-2">Current</span>}
                  </strong>
                  <span className="truncate text-xs text-textDim">
                    {versionSource(v.label)}{v.created_by_name ? `, ${v.created_by_name}` : ''}
                  </span>
                  <time className="text-xs text-textDim" dateTime={ts ? new Date(ts).toISOString() : undefined} title={ts ? formatEasternDateTime(ts) : undefined}>
                    {ts ? formatRelative(ts, at) : ''}
                  </time>
                </div>
                {!current && canRestore && (
                  <button type="button" className="btn-ghost h-7 shrink-0 gap-1.5 px-2.5 text-xs" aria-label={`Restore version ${n}`}
                    onClick={() => onRestore(v, n)}>
                    <RotateCcwIcon size={13} />Restore
                  </button>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </EditorDrawer>
  );
}
